<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DTextAreaField,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon,
  SwitchHorizontal01Icon,
  InfoCircleIcon,
  Loading02Icon,
  AlertHexagonIcon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getVacationRequests,
  VacationRequest,
  canceledVacation,
  deleteVacationRequest,
  IReverseVacation,
  reverseVacation
} from '@/app/core/services/vacation-request'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const showModalReverseVacationRequest = ref(false)
const showModalCanceledVacationRequest = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableVacationRequests = ref(null)
const vacationRequestForDelete = ref()
const urlFile = ref(null)
const refresh = ref()
const isRefreshData = ref<boolean>(false)
const payload = ref()
const dateIni = ref<string>()
const dateEnd = ref<string>()

/* Datos componente table */
const columns = ref([
  {
    name: 'employee_name',
    title: t('vacationRequests.list.employee'),
    table: 'employees.name'
  },
  {
    name: 'normal_days_to_enjoy',
    title: t('vacationRequests.list.normalDays'),
    table: 'vacation_requests.normal_days_to_enjoy'
  },
  {
    name: 'bonus_days_to_enjoy',
    title: t('vacationRequests.list.bonusDays'),
    table: 'vacation_requests.bonus_days_to_enjoy'
  },
  {
    name: 'start_date_to_enjoy',
    title: t('vacationRequests.list.startDate'),
    table: 'vacation_requests.start_date_to_enjoy'
  },
  {
    name: 'end_date_to_enjoy',
    title: t('vacationRequests.list.endDate'),
    table: 'vacation_requests.end_date_to_enjoy'
  },
  { name: 'status', title: t('vacationRequests.list.status'), table: 'status' },
  { name: 'locked', title: t('vacationRequests.list.locked'), table: 'locked' },
  {
    name: 'updated_at',
    title: t('vacationRequests.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<VacationRequest[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Esquema de validacion campos obligatorios */
const validationSchema = object().shape({
  observation: string()
    .required(t('form.valid.required'))
    .min(20, t('form.valid.minLength', [20]))
    .max(500, t('form.valid.maxLength', [500]))
})

const { handleSubmit, errors } = useForm<any>({
  validationSchema
})

/* Definicion de  campos del formulario */
const { value: observation } = useField<string>(ref('observation'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit(values => {
  const payloadReverseVacation: IReverseVacation = {
    vacationRequestId: vacationRequestForDelete.value,
    observation: values.observation
  }
  if(showModalCanceledVacationRequest.value) {
    canceledVacationRequest(payloadReverseVacation)
  } else{
    reverseVacationRequest(payloadReverseVacation)
  }
})

const reverseVacationRequest = async (data: any) => {
  loading.value = true
  try {
    await reverseVacation(data)
    closeModal()
    loading.value = false
    notifications.addNotification({
      title: t('notification.created'),
      text: t('reversePayrolls.notification.action', [
        t('notification.wasCreated')
      ]),
      type: 'success',
      time: 10000
    })
  } catch (error: any) {
    closeModal()
    notificationError(error, 'vacationRequests', t, notifications, loading)
  }
}

const canceledVacationRequest = async (data: any) => {
  loading.value = true
  try {
    await canceledVacation(data)
    closeModal()
    loading.value = false
    notifications.addNotification({
      title: t('notification.created'),
      text: t('reversePayrolls.notification.action', [
        t('notification.wasCreated')
      ]),
      type: 'success',
      time: 10000
    })
  } catch (error: any) {
    closeModal()
    notificationError(error, 'vacationRequests', t, notifications, loading)
  }
}

/* Redirect's */
const createVacationRequest = () => {
  void router.push({ name: 'CreateVacationRequest' })
}
const updateVacationRequest = (id: any) => {
  void router.push({ name: 'UpdateVacationRequest', params: { id } })
}
const getVacationRequest = (id: any) => {
  void router.push({ name: 'GetVacationRequest', params: { id } })
}

/* Eliminar soliccitud vacaciones */
const removeVacationRequest = (values: any) => {
  titleModal.value = t('vacationRequests.list.actionStatus')
  descriptionModal.value = t('modal.description')
  typeAlertModal.value = 'question'
  showModal.value = true
  vacationRequestForDelete.value = values.id
}

const showModalReverseVacation = (values: any) => {
  dateIni.value = dayjs.utc(values.start_date_to_enjoy).format('DD/MM/YYYY')
  dateEnd.value = dayjs.utc(values.end_date_to_enjoy).format('DD/MM/YYYY')
  vacationRequestForDelete.value = values.id
  showModalReverseVacationRequest.value = true
}

const showModalCanceledVacation = (values: any) => {
  dateIni.value = dayjs.utc(values.start_date_to_enjoy).format('DD/MM/YYYY')
  dateEnd.value = dayjs.utc(values.end_date_to_enjoy).format('DD/MM/YYYY')
  vacationRequestForDelete.value = values.id
  showModalCanceledVacationRequest.value = true
  showModalReverseVacationRequest.value = true
}

const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteVacationRequest(vacationRequestForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('vacationRequests.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void vacationRequests()
    })
    .catch((error: any) => {
      notificationError(error, 'vacationRequests', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('id')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('vacationRequests.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'vacationRequests', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  showModalCanceledVacationRequest.value = false
  showModalReverseVacationRequest.value = false
  urlFile.value = null
}

/* Listar solicitud de vacaciones */
const vacationRequests = async (load: boolean = true) => {
  if (load) loading.value = true
  isRefreshData.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getVacationRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
    isRefreshData.value = false
  } catch (error: any) {
    isRefreshData.value = false
    notificationError(error, 'vacationRequests', t, notifications, loading)
  }
}

onMounted(() => {
  refresh.value = setInterval(() => {
    void vacationRequests(false)
  }, 10000)
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>

  <DModal
    :open="showModalReverseVacationRequest"
    :description="descriptionModal"
    :isFooter="false"
    :size="'w-1/4 md:w-2/5 lg:w-1/3 xl:w-1/4 2xl:w-1/4'"
    @closeModal="closeModal">    
    <template v-slot:form>
      <div class="flex flex-col items-center gap-10">
        <section
          class="flex items-center justify-center rounded-full sm:mx-0 h-14 w-14 bg-red-100">
          <InfoCircleIcon class="h-8 w-8 text-red-500" />
        </section>
        <section class="px-5 text-center text-gray-400 text-sm">
          <h1
            class="text-black text-xl"
            v-html="showModalCanceledVacationRequest ? t('vacationRequests.canceledVacationRequest.titleModal') :  t('vacationRequests.reverseVacationRequest.titleModal')" />
          <p class="mt-3">
            {{ showModalCanceledVacationRequest ? t('vacationRequests.canceledVacationRequest.confirmation') : t('vacationRequests.reverseVacationRequest.confirmation') }}
            <span class="text-gray-500 font-bold">{{ showModalCanceledVacationRequest ? t('vacationRequests.canceledVacationRequest.nameProcess') : t('vacationRequests.reverseVacationRequest.nameProcess') }}</span>
            {{ showModalCanceledVacationRequest ? t('vacationRequests.canceledVacationRequest.datePrompt') : t('vacationRequests.reverseVacationRequest.datePrompt') }}
            <span class="text-gray-500 font-bold">
              {{ dateIni }} -
              {{ dateEnd }}
            </span>
            {{ showModalCanceledVacationRequest ? t('vacationRequests.canceledVacationRequest.deleteWarning') : t('vacationRequests.reverseVacationRequest.deleteWarning') }}
          </p>
          <p
            class="mt-5"
            v-html="
              showModalCanceledVacationRequest ? t('vacationRequests.canceledVacationRequest.confirmationMessage') : t('vacationRequests.reverseVacationRequest.confirmationMessage')
            " />
          <DTextAreaField
            v-model="observation"
            :placeholder="t('vacationRequests.fields.reason')"
            :error="errors.observation"
            :rows="7" />
        </section>
        <section class="flex gap-3">
          <button
            @click="closeModal"
            class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
            v-html="t('form.button.cancel')" />
          <button
            @click="onSubmit"
            class="bg-red-100 text-red-800 hover:bg-red-700 hover:text-white focus:ring-red-200 inline-flex text-sm justify-center items-center py-1.5 px-10 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
            v-html="t('form.button.confirm')" />
        </section>
      </div>
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('vacationRequests.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('vacationRequests.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="vacationRequests" />
          <DBtn
            @click.prevent="vacationRequests"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createVacationRequest()"
            v-if="store.hasPermission('vacation_request:create')">
            {{
              t('form.button.create', {
                form: t('vacationRequests.list.title', 1)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableVacationRequests"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), vacationRequests()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), vacationRequests()">
      <template #employee_inactive="data: any">
        <div>
          {{ data.employee_is_inactive === false ? 'No' : 'Si' }}
        </div>
      </template>
      <template #status="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.status === 'Anulado'
              ? 'bg-red-100 text-red-600'
              : data.status === 'Aprobado'
              ? 'bg-green-100 text-green-600'
              : data.status === 'Pendiente'
              ? 'bg-blue-100 text-blue-600'
              : 'bg-gray-100 text-gray-600'
          ">
          {{ t(data.status) }}
        </div>
      </template>
      <template #locked="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.locked == 'true'
              ? 'bg-orange-100 text-orange-600'
              : ''
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.locked == 'true' ? 'bg-orange-600' : ''"></div>
          {{
            data.locked == 'true'
              ? t('vacationRequests.list.lockedReason')
              : t('')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('vacation_request:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getVacationRequest(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              store.hasPermission('vacation_request:update') &&
              data.status !== 'Aprobado' &&
              data.status !== 'Rechazado' &&
              data.locked != 'true'
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateVacationRequest(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>          
          <Popper
            v-if="
              data.origin !== 'MIGRATION' &&
              data.detail !== null &&
              data.detail !== '' &&
              store.hasPermission('vacation_request:reverse') &&
              (data.status != 'Anulado' && (data.status === 'Aprobado' || data.status === 'Rechazado')) &&
              data.locked != 'true'
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.reverse')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="showModalReverseVacation(data)">
              <SwitchHorizontal01Icon
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              data.origin !== 'MIGRATION' &&
              data.detail !== null &&
              data.detail !== '' &&
              data.locked != 'true'
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.canceled')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="showModalCanceledVacation(data)">
              <AlertHexagonIcon
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              store.hasPermission('vacation_request:delete') &&
              data.status !== 'Aprobado' &&
              data.status !== 'Rechazado' &&
              data.status !== 'Anulado' &&
              data.locked != 'true'
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeVacationRequest(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
      <template #refresh v-if="isRefreshData">
        <Loading02Icon class="rotate text-gray-400 mr-2" />
        <h1 class="text-gray-400 text-base" v-html="t('import.list.loading')" />
      </template>
    </DTable>
  </div>
</template>
